import { useCallback, useEffect, useRef, useState } from 'react';
import { useOrganization } from '../../../../contexts/OrganizationContext';
import { supabase } from '../../../../lib/supabase';
import {
  connectWhatsappCoexistence,
  type CoexistenceSignup,
  disconnectWhatsappCloud,
  removeWhatsappCloud,
  repairWhatsappCloudWebhook,
  startWhatsappEmbeddedSignup,
  syncWhatsappCloud,
  takeWhatsappSignupReturn,
} from '../../../../services/modules/whatsappCloudService';
import { notifyWhatsappConnectionsUpdated } from '../../../../services/modules/whatsappService';
import { notify } from '../../../../utils/toast';

export type CloudConnection = {
  id: string;
  name: string;
  status: string;
  displayNumber: string;
  syncWarning: string;
  canRetrySync: boolean;
  coexistence: boolean;
};

function readConfigText(config: Record<string, unknown> | null, key: string): string {
  const value = config?.[key];
  return typeof value === 'string' ? value : '';
}

export function useWhatsappCoexistence() {
  const { currentOrganization } = useOrganization();
  const organizationId = currentOrganization?.id;
  const isAdmin = currentOrganization?.role === 'owner' || currentOrganization?.role === 'admin';
  const [connection, setConnection] = useState<CloudConnection | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const webhookRepairStarted = useRef<string | null>(null);

  const load = useCallback(async () => {
    if (!organizationId) {
      setConnection(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    const { data, error } = await supabase
      .from('integrations')
      .select('id, name, status, config')
      .eq('organization_id', organizationId)
      .eq('type', 'whatsapp_cloud')
      .maybeSingle();

    if (error) {
      console.error('[WhatsappCoexistence] load', error.message);
      setIsLoading(false);
      return;
    }

    const config = (data?.config || null) as Record<string, unknown> | null;
    const syncWarning = [readConfigText(config, 'sync_warning'), readConfigText(config, 'history_sync_note')]
      .filter(Boolean)
      .join(' ');
    setConnection(
      data
        ? {
            id: data.id,
            name: data.name,
            status: data.status === 'connected' ? 'Conectado' : 'Desconectado',
            displayNumber: readConfigText(config, 'display_number'),
            syncWarning,
            canRetrySync: data.status === 'connected' && Boolean(syncWarning),
            coexistence: config?.coexistence === true || config?.is_on_biz_app === true,
          }
        : null,
    );
    setIsLoading(false);
  }, [organizationId]);

  const finishConnect = useCallback(async (signup: CoexistenceSignup) => {
    if (!organizationId) return;
    setIsConnecting(true);
    try {
      const result = await connectWhatsappCoexistence(organizationId, signup);
      notify.success(
        result.coexistence
          ? `${result.name} conectado. O app no celular continua ativo.`
          : `${result.name} conectado.`,
      );
      if (result.warnings.length) notify.info(result.warnings[0]);
      notifyWhatsappConnectionsUpdated();
      await load();
    } catch (error) {
      notify.error(error instanceof Error ? error.message : 'Erro ao conectar WhatsApp Business');
    } finally {
      setIsConnecting(false);
    }
  }, [load, organizationId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!organizationId || !isAdmin || !connection) return;
    if (!connection.syncWarning.includes('Webhook do app não configurado no servidor')) return;
    if (webhookRepairStarted.current === connection.id) return;
    webhookRepairStarted.current = connection.id;
    void repairWhatsappCloudWebhook(organizationId, connection.id)
      .then((result) => {
        if (result.warning) notify.info(result.warning);
        return load();
      })
      .catch((error) => {
        notify.info(error instanceof Error ? error.message : 'Webhook do app não configurado no servidor');
      });
  }, [connection, isAdmin, load, organizationId]);

  useEffect(() => {
    if (!organizationId) return;
    const pending = takeWhatsappSignupReturn();
    if (!pending) return;
    if (!pending.ok) {
      notify.error(pending.message);
      return;
    }
    void finishConnect(pending.signup);
  }, [finishConnect, organizationId]);

  const handleConnect = () => {
    if (!organizationId) {
      notify.error('Organização não encontrada');
      return;
    }
    try {
      startWhatsappEmbeddedSignup();
    } catch (error) {
      notify.error(error instanceof Error ? error.message : 'Erro ao conectar WhatsApp Business');
    }
  };

  const handleDisconnect = async () => {
    if (!organizationId || !connection || isDisconnecting) return;
    setIsDisconnecting(true);
    try {
      const result = await disconnectWhatsappCloud(organizationId, connection.id);
      if (result.warning) notify.info(result.warning);
      else notify.success('Desconectado da API oficial. O WhatsApp Business no celular continua ativo.');
      notifyWhatsappConnectionsUpdated();
      await load();
    } catch (error) {
      notify.error(error instanceof Error ? error.message : 'Erro ao desconectar');
    } finally {
      setIsDisconnecting(false);
    }
  };

  const handleRemove = async () => {
    if (!organizationId || !connection) return;
    try {
      await removeWhatsappCloud(organizationId, connection.id);
      notify.success('Conexão removida');
      notifyWhatsappConnectionsUpdated();
      await load();
    } catch (error) {
      notify.error(error instanceof Error ? error.message : 'Erro ao remover a conexão');
    }
  };

  const handleSync = async () => {
    if (!organizationId || !connection) return;
    setIsSyncing(true);
    try {
      const result = await syncWhatsappCloud(organizationId, connection.id);
      if (result.warnings.length) notify.info(result.warnings[0]);
      else notify.success('Sincronização pedida de novo.');
      await load();
    } catch (error) {
      notify.error(error instanceof Error ? error.message : 'Erro ao sincronizar');
    } finally {
      setIsSyncing(false);
    }
  };

  return {
    connection,
    isAdmin,
    isLoading,
    isConnecting,
    isDisconnecting,
    isSyncing,
    handleConnect,
    handleDisconnect,
    handleRemove,
    handleSync,
  };
}
